import React, { useState } from "react";
import { Instagram, Mail } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import PatternOverlay from "@/components/PatternOverlay";

const INSTAGRAM_URL =
  "https://www.instagram.com/aexents?igsh=MTB2ZG9send0Nm1keA%3D%3D&utm_source=qr";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", subject: "general", message: "" });
  const [status, setStatus] = useState("idle");
  const [feedback, setFeedback] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setFeedback("");
    try {
      // Route through the subscribe endpoint with a distinct source so messages
      // land alongside the mailing list until a dedicated inbox is wired up.
      await api.post("/subscribe", {
        email: form.email,
        source: `contact_${form.subject}`,
      });
      setStatus("success");
      setFeedback("Thank you. We've received your note — expect a reply within one working day.");
      setForm({ name: "", email: "", subject: "general", message: "" });
    } catch (err) {
      setStatus("error");
      setFeedback(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    }
  };

  const input = "w-full bg-transparent border-b border-border py-3 focus:outline-none focus:border-primary text-sm placeholder:text-muted-foreground";

  return (
    <div className="pattern-bg w-full" data-testid="contact-page">
      <PatternOverlay opacity={0.09} size={520} />
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="text-xs uppercase tracking-[0.32em] text-primary">Correspondence</div>
        <h1 className="font-serif text-5xl md:text-6xl mt-4 tracking-tight leading-[1.02]">
          Get in <span className="italic text-primary">touch</span>.
        </h1>
        <p className="mt-6 text-muted-foreground leading-relaxed max-w-xl">
          For orders, layering guidance, press or partnerships — we'd love to
          hear from you. Our atelier responds within one working day.
        </p>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-5 gap-10">
          {/* Direct channels */}
          <aside className="md:col-span-2 space-y-8">
            <div>
              <div className="text-xs uppercase tracking-[0.28em] text-primary mb-3">Email</div>
              <a href="mailto:care@aexents.com" data-testid="contact-email" className="text-lg hover:text-primary transition-colors inline-flex items-center gap-2">
                <Mail size={16} /> care@aexents.com
              </a>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.28em] text-primary mb-3">Press &amp; partnerships</div>
              <a href="mailto:press@aexents.com" data-testid="contact-press" className="text-lg hover:text-primary transition-colors inline-flex items-center gap-2">
                <Mail size={16} /> press@aexents.com
              </a>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.28em] text-primary mb-3">Follow</div>
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram" className="text-lg hover:text-primary transition-colors inline-flex items-center gap-2">
                <Instagram size={16} /> @aexents
              </a>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.28em] text-primary mb-3">Atelier</div>
              <div className="text-sm text-muted-foreground leading-relaxed">
                æxènts London<br />
                By appointment only
              </div>
            </div>
          </aside>

          {/* Form */}
          <form onSubmit={submit} className="md:col-span-3 space-y-6" data-testid="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <input required placeholder="Your name" value={form.name} onChange={set("name")} data-testid="contact-name" className={input} />
              <input type="email" required placeholder="Email" value={form.email} onChange={set("email")} data-testid="contact-emailfield" className={input} />
            </div>
            <select value={form.subject} onChange={set("subject")} data-testid="contact-subject" className={`${input} appearance-none`}>
              <option value="general" className="bg-background">General enquiry</option>
              <option value="order" className="bg-background">Order &amp; delivery</option>
              <option value="layering" className="bg-background">Layering guidance</option>
              <option value="press" className="bg-background">Press &amp; partnerships</option>
              <option value="wholesale" className="bg-background">Wholesale</option>
            </select>
            <textarea required rows={5} placeholder="Your message" value={form.message} onChange={set("message")} data-testid="contact-message" className={`${input} resize-none`} />

            {feedback && (
              <div data-testid="contact-feedback" className={`text-sm ${status === "success" ? "text-primary" : "text-destructive"}`}>
                {feedback}
              </div>
            )}

            <button
              type="submit"
              disabled={status === "loading"}
              data-testid="contact-submit"
              className="bg-primary text-primary-foreground uppercase tracking-[0.22em] text-xs px-10 py-4 hover:opacity-90 disabled:opacity-60 transition-opacity"
            >
              {status === "loading" ? "Sending…" : "Send Message"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
